import React, { useState, useEffect } from 'react';
import './NavbarWallet.css';
import ApiService from '../services/api';
import MetaMaskService from '../services/metamask';
import { useBotData } from '../hooks/useBotData';

const NavbarWallet = ({ walletConnected, walletAddress }) => {
  const { botData, isConnected, getPortfolioData } = useBotData();
  const [walletData, setWalletData] = useState({
    balance: 0,
    usdcBalance: 0,
    totalValue: 0,
    pnl: 0,
    pnlPercent: 0,
    scalpingProfits: 0,
    position: null,
    isRunning: false
  });
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Mettre à jour les données du portefeuille
  const updateWalletData = async () => {
    if (!walletConnected) return;

    try {
      setIsLoading(true);
      
      // Priorité ABSOLUE aux données du bot Python si connecté
      if (isConnected && botData) {
        const portfolioData = getPortfolioData();
        setWalletData({
          balance: portfolioData.balance || 0,
          usdcBalance: portfolioData.usdcBalance || 0,
          totalValue: portfolioData.totalValue || 0,
          pnl: portfolioData.pnl || 0,
          pnlPercent: portfolioData.pnlPercent || 0,
          scalpingProfits: portfolioData.scalpingProfits || 0,
          position: portfolioData.position,
          isRunning: portfolioData.isRunning
        });
        // Log seulement occasionnellement
        if (Math.random() < 0.05) {
          console.log('💰 Données portefeuille mises à jour depuis le bot Python');
        }
        return; // SORTIR ICI pour éviter l'API SnowCash
      }
      
      // Si bot Python non connecté, afficher ZÉRO partout (pas de simulation)
      if (!isConnected) {
        setWalletData({
          balance: 0,
          usdcBalance: 0,
          totalValue: 0,
          pnl: 0,
          pnlPercent: 0,
          scalpingProfits: 0,
          position: null,
          isRunning: false
        });
        console.log('🔴 Bot Python déconnecté - Affichage zéros (pas de simulation)');
      }
    } catch (error) {
      console.error('❌ Erreur mise à jour portefeuille:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mise à jour automatique toutes les 5 secondes (SEULEMENT si bot Python non connecté)
  useEffect(() => {
    if (walletConnected && !isConnected) {
      updateWalletData();
      
      const interval = setInterval(updateWalletData, 5000);
      return () => clearInterval(interval);
    }
  }, [walletConnected, isConnected]);

  // Mise à jour immédiate quand les données du bot changent
  useEffect(() => {
    if (walletConnected && isConnected && botData) {
      updateWalletData();
    }
  }, [botData, isConnected]);

  // Formater les montants
  const formatAmount = (amount) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount.toFixed(2);
  };

  // Formater l'adresse
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  if (!walletConnected) return null;

  return (
    <>
      <div className="navbar-wallet" onClick={() => setShowWalletModal(true)}>
        <div className="wallet-icon">
          {isLoading ? (
            <div className="loading-spinner">⏳</div>
          ) : (
            <div className="wallet-emoji">
              {isConnected ? '🤖' : '💼'}
            </div>
          )}
        </div>
        
        <div className="wallet-info">
          <div className="wallet-balance">
            💰 ${walletData.balance.toFixed(2)}
          </div>
          <div className={`wallet-pnl ${walletData.pnl >= 0 ? 'positive' : 'negative'}`}>
            ${walletData.pnl >= 0 ? '+' : ''}{walletData.pnl.toFixed(2)}({walletData.pnlPercent >= 0 ? '+' : ''}{walletData.pnlPercent.toFixed(2)}%)
          </div>
        </div>

        <div className="wallet-arrow">
          <span>▼</span>
        </div>
      </div>

      {/* Modal du portefeuille */}
      {showWalletModal && (
        <div className="wallet-modal-overlay" onClick={() => setShowWalletModal(false)}>
          <div className="wallet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wallet-modal-header">
              <h3>{isConnected ? '🤖 Bot Trading Live' : '💼 Mon Portefeuille'}</h3>
              <div className="connection-status">
                <span className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></span>
                <span className="status-text">
                  {isConnected ? 'Bot Connecté' : 'Mode Démo'}
                </span>
              </div>
              <button 
                className="close-button"
                onClick={() => setShowWalletModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="wallet-modal-content">
              {/* Adresse du wallet */}
              <div className="wallet-address-section">
                <div className="address-label">Adresse:</div>
                <div className="address-value">
                  {formatAddress(walletAddress)}
                  <button 
                    className="copy-btn"
                    onClick={() => navigator.clipboard.writeText(walletAddress)}
                    title="Copier l'adresse"
                  >
                    📋
                  </button>
                </div>
              </div>

              {/* Données du bot si connecté */}
              {isConnected && walletData.position && (
                <div className="bot-section">
                  <div className="bot-status">
                    <div className="status-indicator">
                      <span className={`bot-status-dot ${walletData.isRunning ? 'running' : 'stopped'}`}></span>
                      <span>Bot {walletData.isRunning ? 'EN COURS' : 'ARRÊTÉ'}</span>
                    </div>
                    <div className="position-info">
                      <span>📍 Position: {walletData.position.size?.toFixed(3)} SOL</span>
                      <span>💰 Prix: ${walletData.position.current_price?.toFixed(4)}</span>
                    </div>
                  </div>
                  
                  {walletData.scalpingProfits > 0 && (
                    <div className="scalping-profits">
                      <div className="scalping-label">⚡ Profits Scalping</div>
                      <div className="scalping-value">+${formatAmount(walletData.scalpingProfits)}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Balances */}
              <div className="balances-section">
                <div className="balance-item">
                  <div className="balance-label">💰 Balance {isConnected ? 'Bot' : 'Hyperliquid'}</div>
                  <div className="balance-value">${formatAmount(walletData.balance)}</div>
                </div>
                
                <div className="balance-item">
                  <div className="balance-label">🪙 USDC Disponible</div>
                  <div className="balance-value">${formatAmount(walletData.usdcBalance)}</div>
                </div>
                
                <div className="balance-item total">
                  <div className="balance-label">📊 Valeur Totale</div>
                  <div className="balance-value">${formatAmount(walletData.totalValue)}</div>
                </div>
              </div>

              {/* PnL */}
              <div className="pnl-section">
                <div className="pnl-label">📈 Profit & Loss</div>
                <div className={`pnl-value ${walletData.pnl >= 0 ? 'positive' : 'negative'}`}>
                  {walletData.pnl >= 0 ? '+' : ''}${formatAmount(walletData.pnl)}
                  <span className="pnl-percent">
                    ({walletData.pnlPercent >= 0 ? '+' : ''}{walletData.pnlPercent.toFixed(2)}%)
                  </span>
                </div>
              </div>

              {/* Actions rapides */}
              <div className="wallet-actions">
                <button className="action-btn deposit-btn">
                  💸 Déposer
                </button>
                <button className="action-btn withdraw-btn">
                  💰 Retirer
                </button>
                <button className="action-btn refresh-btn" onClick={updateWalletData}>
                  🔄 Actualiser
                </button>
              </div>

              {/* Dernière mise à jour */}
              <div className="last-update">
                Dernière mise à jour: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NavbarWallet;
